import {
  userStatePath,
  type UserDataLocator,
  type UserStateRecord,
} from "#bb/lib/userState";

export const USER_STATE_CONTENT_TYPE =
  "application/vnd.pivotalgroup.data.user-state+json";

interface CandidateWriteExecutor {
  <T>(
    context: {
      operation: "user-state";
      targetPath: string;
    },
    write: () => Promise<T>,
  ): Promise<T>;
}

function isNotFoundError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const candidate = error as {
    status?: number;
    statusCode?: number;
    response?: { status?: number };
    data?: { status?: number; statusCode?: number };
  };
  return (
    candidate.statusCode === 404
    || candidate.status === 404
    || candidate.response?.status === 404
    || candidate.data?.statusCode === 404
    || candidate.data?.status === 404
  );
}

function splitFilePath(path: string): { directory: string; fileName: string } {
  const segments = path.split("/");
  const fileName = segments.pop();
  if (!fileName) {
    throw new Error(`Invalid user state path: ${path}`);
  }
  return { directory: segments.join("/"), fileName };
}

export function useUserStateStorage() {
  const { siteId } = useSite();
  const { profile, getProfile } = useProfile();
  const candidateWriteReliably = (
    useNuxtApp() as unknown as {
      $candidateWriteReliably?: CandidateWriteExecutor;
    }
  ).$candidateWriteReliably;

  async function resolveContext() {
    const currentProfile = profile.value ?? await getProfile();
    const resolvedSiteId = siteId.value?.trim();
    const userId = String(currentProfile?.id ?? "").trim();

    if (!resolvedSiteId) {
      throw new Error("A site ID is required to access user state.");
    }
    if (!userId) {
      throw new Error("A candidate profile is required to access user state.");
    }
    return { siteId: resolvedSiteId, userId };
  }

  async function load(locator: UserDataLocator): Promise<UserStateRecord | null> {
    const context = await resolveContext();
    const path = userStatePath(
      context.userId,
      locator.dataPath,
      locator.key,
    );

    try {
      const raw = await useApiFetch<string>(
        `/v1/file/${context.siteId}/download/${path}`,
        { responseType: "text" },
      );
      return JSON.parse(raw) as UserStateRecord;
    } catch (error) {
      if (isNotFoundError(error)) return null;
      throw error;
    }
  }

  async function save(
    locator: UserDataLocator,
    record: UserStateRecord,
  ): Promise<void> {
    const context = await resolveContext();
    const path = userStatePath(
      context.userId,
      locator.dataPath,
      locator.key,
    );
    const { directory, fileName } = splitFilePath(path);

    const write = () =>
      useApiFetch(`/v1/file/${directory}`, {
        method: "POST",
        headers: {
          "x-site-id": context.siteId,
          "Content-Disposition": `form-data; name="state"; filename="${fileName}"`,
          "Content-Type": USER_STATE_CONTENT_TYPE,
          "x-mkdir": true,
        },
        body: JSON.stringify(record),
      });

    if (candidateWriteReliably) {
      await candidateWriteReliably(
        {
          operation: "user-state",
          targetPath: path,
        },
        write,
      );
      return;
    }

    await write();
  }

  return { load, save };
}
